import { useState, useId } from 'react';
import { X, MessageCircle, Sparkles, Building2, ChevronDown, ChevronUp, AlertTriangle } from 'lucide-react';
import { api } from '../services/api';
import { useToast } from '../contexts/ToastContext';
import CreatableSelect from './CreatableSelect';
import QualificacaoTab from './QualificacaoTab';
import InteractionsTab from './InteractionsTab';
import AuditTab from './AuditTab';
import ProposalsTab from './ProposalsTab';
import LeadTimelineTab from './LeadTimelineTab';
import WhatsAppModal from './WhatsAppModal';
import CustomFieldsSection from './CustomFieldsSection';
import ContatosTab from './ContatosTab';
import EmpresaTab from './EmpresaTab';
import ContaFormModal from './ContaFormModal';
import { useAuth } from '../contexts/AuthContext';
import { useLeadForm } from './lead-form/useLeadForm';
import {
  formatBRL,
  CurrencyInput,
  RazaoSocialAutocomplete,
  LeadSearch,
  ContaSearch,
  ParcelamentoBlock,
} from './lead-form/LeadFormSubcomponents';

const ESTADOS = [
  'AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG',
  'PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO'
];

export default function LeadFormModal({ isOpen, onClose, onSave, lead, funnelType, esns, products, contas = [], leads = [], onCreateEsn, onCreateProduct, initialConta = null }) {
  const toast = useToast();
  const { user } = useAuth();
  const isEditing = !!lead?.id;

  // UI state que fica no modal
  const [activeTab, setActiveTab] = useState('dados');
  const [showWA, setShowWA] = useState(false);
  const [aiSummary, setAiSummary] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiType, setAiType] = useState('summary');
  const [selectedContaForModal, setSelectedContaForModal] = useState(null);
  const [activeContaTab, setActiveContaTab] = useState('dados');

  // Lógica de formulário extraída para hook
  const {
    form, errors, setErrors,
    cepLoading, cnpjLoading, origins, insightsOpen, setInsightsOpen,
    contaAlertMatriz, contaAlertFiliais, duplicateLeads, contaCnpjPendente,
    handleChange, handleLeadSelect, handleContaSelect, handleSubmit,
    fetchCep, fetchCnpj,
    inputClass, filledCount, completudePct, completudeColor, COMPLETUDE_FIELDS,
  } = useLeadForm({ lead, funnelType, onSave, contas, leads, isOpen, initialConta });

  const fieldId = useId();
  const fid = {
    contaLegend: `${fieldId}-legend-conta`,
    contaBusca: `${fieldId}-conta-busca`,
    cnpjEmpresa: `${fieldId}-cnpj-emp`,
    cnpjConta: `${fieldId}-cnpj-conta`,
    razaoSocial: `${fieldId}-razao`,
    nomeFantasia: `${fieldId}-nome-fant`,
    titulo: `${fieldId}-titulo`,
    tituloCompact: `${fieldId}-titulo-compact`,
    enderecoCep: `${fieldId}-cep`,
    enderecoRua: `${fieldId}-rua`,
    enderecoNumero: `${fieldId}-num`,
    enderecoBairro: `${fieldId}-bairro`,
    cidade: `${fieldId}-cidade`,
    estado: `${fieldId}-estado`,
    telefoneEmpresa: `${fieldId}-tel-emp`,
    contatoNome: `${fieldId}-ct-nome`,
    contatoFuncao: `${fieldId}-ct-func`,
    contatoTelefone: `${fieldId}-ct-tel`,
    contatoEmail: `${fieldId}-ct-email`,
    valorSaas: `${fieldId}-valor-saas`,
    valorAdesao: `${fieldId}-valor-ades`,
    valorServico: `${fieldId}-valor-serv`,
    previsao: `${fieldId}-previsao`,
  };

  const handleOpenConta = async (contaId) => {
    try {
      const fullConta = await api.getConta(contaId);
      setSelectedContaForModal(fullConta);
      setActiveContaTab('receita');
    } catch (err) {
      toast.error('Erro ao carregar conta: ' + err.message);
    }
  };

  // Após migration 029: esns_view usa users.id mas leads.esn_id guarda legacy esns.id
  const esnOptions = (() => {
    const base = esns.map(e => ({ ...e, id: Number(e.legacy_esn_id ?? e.esn_id ?? e.id) }));
    const assignedId = Number(lead?.esn_id) || Number(form.esn_id);
    if (assignedId && !base.find(o => o.id === assignedId)) {
      base.unshift({ id: assignedId, nome: lead?.esn_nome || `ESN #${assignedId}` });
    }
    return base;
  })();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40">
      <div className="bg-white sm:rounded-xl shadow-2xl w-full sm:max-w-2xl h-full max-sm:max-h-[100dvh] sm:h-auto sm:max-h-[90vh] overflow-y-auto rounded-t-2xl max-sm:pt-[env(safe-area-inset-top)] max-sm:pb-[max(1rem,env(safe-area-inset-bottom))]">

        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-lg font-semibold text-[#535c69]">
              {lead ? 'Editar Lead' : funnelType === 'base' ? 'Novo Lead BASE' : 'Novo Lead'}
            </h2>
            <button type="button" onClick={onClose} className="p-1 hover:bg-gray-100 rounded"><X size={20} /></button>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
              <div className="h-full rounded-full transition-all duration-300"
                style={{ width: `${completudePct}%`, backgroundColor: completudeColor }} />
            </div>
            <span className="text-[11px] font-medium whitespace-nowrap" style={{ color: completudeColor }}>
              {filledCount}/{COMPLETUDE_FIELDS.length} campos
            </span>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 px-6 overflow-x-auto scrollbar-none">
          {[
            { key: 'dados', label: 'Dados' },
            { key: 'contatos', label: 'Contatos' },
            { key: 'qualificacao', label: 'Qualificação' },
            ...(lead ? [{ key: 'interacoes', label: 'Interações' }] : []),
            ...(lead ? [{ key: 'propostas', label: 'Propostas' }] : []),
            ...(lead && form.conta_id ? [{ key: 'empresa', label: '🏢 Empresa' }] : []),
            ...(lead ? [{ key: 'historico', label: 'Histórico' }] : []),
            ...(lead ? [{ key: 'timeline', label: 'Timeline' }] : []),
            ...(lead ? [{ key: 'ia', label: '✨ IA' }] : []),
          ].map(tab => (
            <button key={tab.key} type="button" onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                activeTab === tab.key ? 'border-[#00DBFF] text-[#00DBFF]' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}>
              {tab.label}
            </button>
          ))}
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4" data-lead-form>

          {/* ── Abas secundárias ── */}
          {activeTab === 'contatos' && (
            <ContatosTab contaId={form.conta_id || null}
              leadPrincipal={form.contato_nome ? { nome: form.contato_nome, cargo: form.contato_funcao || '', telefone: form.contato_telefone || '', email: form.contato_email || '' } : null}
              emptyHint={isEditing
                ? 'Este lead ainda não está vinculado a uma conta. Volte à aba Dados e selecione/crie uma conta para gerenciar contatos.'
                : 'Salve o lead primeiro (aba Dados) para gerenciar contatos.'} />
          )}
          {activeTab === 'qualificacao' && <QualificacaoTab form={form} onChange={handleChange} />}
          {activeTab === 'interacoes' && lead && <InteractionsTab lead={lead} esns={esns} />}
          {activeTab === 'propostas' && lead && <ProposalsTab lead={lead} />}
          {activeTab === 'empresa' && lead && <EmpresaTab lead={lead} onOpenConta={handleOpenConta} />}
          {activeTab === 'historico' && lead && <AuditTab lead={lead} />}
          {activeTab === 'timeline' && lead && <LeadTimelineTab lead={lead} />}

          {activeTab === 'ia' && lead && (
            <div className="space-y-4 py-2">
              <div className="flex items-center gap-2 flex-wrap">
                <select value={aiType} onChange={e => setAiType(e.target.value)}
                  className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm">
                  <option value="summary">Resumo executivo</option>
                  <option value="email">Rascunho de e-mail</option>
                </select>
                <button type="button" onClick={async () => {
                  setAiLoading(true); setAiSummary('');
                  try { const data = await api.getAiSummary(lead.id, aiType); setAiSummary(data.text); }
                  catch (err) { setAiSummary(`Erro: ${err.message}`); }
                  finally { setAiLoading(false); }
                }} disabled={aiLoading}
                  className="flex items-center gap-1.5 px-4 py-1.5 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors">
                  <Sparkles size={14} />
                  {aiLoading ? 'Gerando...' : 'Gerar com IA'}
                </button>
              </div>
              {aiSummary && (
                <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
                  <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{aiSummary}</p>
                  <button type="button" onClick={() => navigator.clipboard?.writeText(aiSummary)}
                    className="mt-2 text-xs text-purple-500 hover:text-purple-700">Copiar</button>
                </div>
              )}
              {!aiSummary && !aiLoading && (
                <p className="text-sm text-gray-400">Selecione o tipo e clique em "Gerar com IA" para obter um texto gerado automaticamente com base nas interações e dados do lead.</p>
              )}
            </div>
          )}

          {/* ── Aba Dados ── */}
          {activeTab === 'dados' && (<>

            {form.situacao_cadastral && form.situacao_cadastral.toUpperCase() !== 'ATIVA' && (
              <div className="rounded-lg bg-red-50 border border-red-300 p-3 text-red-700 text-xs font-semibold flex items-center gap-2 shadow-[0_0_8px_rgba(239,68,68,0.15)]">
                <AlertTriangle size={16} className="text-red-600 shrink-0" />
                <div>
                  <p className="font-bold text-red-800">CNPJ Inativo (Situação: {form.situacao_cadastral})</p>
                  <p className="text-red-600 font-normal">Este cliente está com o CNPJ inativo na Receita Federal e não pode ser cadastrado ou modificado.</p>
                </div>
              </div>
            )}

            {!isEditing && leads.length > 0 && (
              <LeadSearch leads={leads} onSelect={handleLeadSelect} />
            )}

            {/* ZYN-306: aviso não-bloqueante de oportunidade ativa com o mesmo CNPJ */}
            {!isEditing && duplicateLeads.length > 0 && (
              <div className="rounded-lg bg-amber-50 border border-amber-300 p-3 text-xs">
                <p className="font-bold text-amber-800 flex items-center gap-1.5">
                  <AlertTriangle size={14} className="shrink-0" />
                  Já existe oportunidade ativa para este CNPJ
                </p>
                <ul className="mt-1.5 space-y-1">
                  {duplicateLeads.map(d => (
                    <li key={d.id}>
                      <a
                        href={`${d.funnel_type === 'base' ? '/base' : '/new-names'}?openLead=${d.id}`}
                        target="_blank" rel="noopener noreferrer"
                        className="text-amber-700 hover:text-amber-900 underline"
                      >
                        {d.titulo || d.razao_social}
                      </a>
                      <span className="text-amber-600">
                        {' '}({d.funnel_type === 'base' ? 'Base' : 'Novos'} · {d.stage}{d.esn_nome ? ` · ${d.esn_nome}` : ''})
                      </span>
                    </li>
                  ))}
                </ul>
                <p className="text-amber-600 mt-1.5">Verifique antes de criar uma duplicata — você ainda pode prosseguir se for uma oportunidade distinta.</p>
              </div>
            )}

            <fieldset>
              <legend id={fid.contaLegend} className="text-sm font-semibold text-[#535c69] mb-2">Conta *</legend>
              <ContaSearch contas={contas} value={form.conta_id || null}
                onChange={(id) => { handleChange('conta_id', id); if (id) { const c = contas.find(x => x.id === id); if (c) handleContaSelect(c); } }}
                className={inputClass('conta_id')} inputId={fid.contaBusca} ariaLabelledby={fid.contaLegend}
                formHints={{ razao_social: form.razao_social, nome_fantasia: form.nome_fantasia, cnpj: form.cnpj, cidade: form.cidade, estado: form.estado, telefone_empresa: form.telefone_empresa, esn_id: form.esn_id }}
              />
              {errors.conta_id && <p className="text-xs text-red-500 mt-1">{errors.conta_id}</p>}
              {!form.conta_id && <p className="text-xs text-gray-500 mt-1">Busque uma conta existente por nome, razão social ou CNPJ — ou cadastre uma nova. Toda oportunidade precisa estar vinculada a um cliente.</p>}
            </fieldset>

            {!form.conta_id && (
            <fieldset className="space-y-3">
              <legend className="text-sm font-semibold text-[#535c69] mb-2">Dados da Empresa</legend>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label htmlFor={fid.cnpjEmpresa} className="block text-xs font-medium text-gray-600 mb-1">
                    CNPJ {cnpjLoading && <span className="text-[#00DBFF]">buscando…</span>} *
                  </label>
                  <input id={fid.cnpjEmpresa} type="text" value={form.cnpj || ''} onChange={e => handleChange('cnpj', e.target.value)} onBlur={e => fetchCnpj(e.target.value)} placeholder="00.000.000/0001-00" className={inputClass('cnpj')} />
                  {errors.cnpj && <p className="text-xs text-red-500 mt-1">{errors.cnpj}</p>}
                </div>
                <div>
                  <label htmlFor={fid.razaoSocial} className="block text-xs font-medium text-gray-600 mb-1">Razão Social *</label>
                  <RazaoSocialAutocomplete id={fid.razaoSocial} value={form.razao_social || ''} onChange={val => handleChange('razao_social', val)} onSelect={handleContaSelect} contas={contas} className={inputClass('razao_social')} />
                  {errors.razao_social && <p className="text-xs text-red-500 mt-1">{errors.razao_social}</p>}
                </div>
                <div>
                  <label htmlFor={fid.nomeFantasia} className="block text-xs font-medium text-gray-600 mb-1">Nome Fantasia</label>
                  <input id={fid.nomeFantasia} type="text" value={form.nome_fantasia || ''} onChange={e => handleChange('nome_fantasia', e.target.value)} className={inputClass('nome_fantasia')} />
                </div>
                <div>
                  <label htmlFor={fid.titulo} className="block text-xs font-medium text-gray-600 mb-1">Título do Lead</label>
                  <input id={fid.titulo} type="text" value={form.titulo || ''} onChange={e => handleChange('titulo', e.target.value)} className={inputClass('titulo')} />
                </div>
              </div>
              <div className="grid grid-cols-4 gap-3">
                <div>
                  <label htmlFor={fid.enderecoCep} className="block text-xs font-medium text-gray-600 mb-1">CEP {cepLoading && <span className="text-[#00DBFF]">buscando…</span>}</label>
                  <input id={fid.enderecoCep} type="text" value={form.endereco_cep || ''} onChange={e => handleChange('endereco_cep', e.target.value)} onBlur={e => fetchCep(e.target.value)} placeholder="00000-000" className={inputClass('endereco_cep')} />
                  {errors.endereco_cep && <p className="text-xs text-red-500 mt-1">{errors.endereco_cep}</p>}
                </div>
                <div className="col-span-2">
                  <label htmlFor={fid.enderecoRua} className="block text-xs font-medium text-gray-600 mb-1">Rua</label>
                  <input id={fid.enderecoRua} type="text" value={form.endereco_rua || ''} onChange={e => handleChange('endereco_rua', e.target.value)} className={inputClass('endereco_rua')} />
                </div>
                <div>
                  <label htmlFor={fid.enderecoNumero} className="block text-xs font-medium text-gray-600 mb-1">Número</label>
                  <input id={fid.enderecoNumero} type="text" value={form.endereco_numero || ''} onChange={e => handleChange('endereco_numero', e.target.value)} className={inputClass('endereco_numero')} />
                </div>
              </div>
              <div className="grid grid-cols-4 gap-3">
                <div>
                  <label htmlFor={fid.enderecoBairro} className="block text-xs font-medium text-gray-600 mb-1">Bairro</label>
                  <input id={fid.enderecoBairro} type="text" value={form.endereco_bairro || ''} onChange={e => handleChange('endereco_bairro', e.target.value)} className={inputClass('endereco_bairro')} />
                </div>
                <div>
                  <label htmlFor={fid.cidade} className="block text-xs font-medium text-gray-600 mb-1">Cidade</label>
                  <input id={fid.cidade} type="text" value={form.cidade || ''} onChange={e => handleChange('cidade', e.target.value)} className={inputClass('cidade')} />
                </div>
                <div>
                  <label htmlFor={fid.estado} className="block text-xs font-medium text-gray-600 mb-1">Estado</label>
                  <select id={fid.estado} value={form.estado || ''} onChange={e => handleChange('estado', e.target.value)} className={inputClass('estado')}>
                    <option value="">UF</option>
                    {ESTADOS.map(uf => <option key={uf} value={uf}>{uf}</option>)}
                  </select>
                </div>
                <div>
                  <label htmlFor={fid.telefoneEmpresa} className="block text-xs font-medium text-gray-600 mb-1">Telefone</label>
                  <input id={fid.telefoneEmpresa} type="text" value={form.telefone_empresa || ''} onChange={e => handleChange('telefone_empresa', e.target.value)} placeholder="(00) 00000-0000" className={inputClass('telefone_empresa')} />
                </div>
              </div>
            </fieldset>
            )}

            {form.conta_id && (
              <div className="space-y-3">
                <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Conta vinculada</p>
                      <p className="text-sm font-semibold text-[#002233] truncate">{form.razao_social || form.nome_fantasia || '—'}</p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {contaCnpjPendente ? <span className="text-amber-700 font-medium">CNPJ não informado</span> : (form.cnpj || '—')}
                        {form.cidade && ` · ${form.cidade}${form.estado ? `/${form.estado}` : ''}`}
                      </p>
                    </div>
                    <div className="flex-shrink-0">
                      <label htmlFor={fid.tituloCompact} className="block text-xs font-medium text-gray-600 mb-1">Título do Lead</label>
                      <input id={fid.tituloCompact} type="text" value={form.titulo || ''} onChange={e => handleChange('titulo', e.target.value)} className={`${inputClass('titulo')} w-48`} placeholder={form.razao_social} />
                    </div>
                  </div>
                </div>
                {/* A conta veio da importação de realizados sem CNPJ (placeholder
                    `IMP_<hash>`) ou com DV inválido. O lead exige CNPJ válido —
                    no cliente e no servidor —, então pedimos aqui, no único lugar
                    onde o usuário está olhando. Sem isso o erro caía num input que
                    nem era renderizado e a oportunidade ficava impossível de criar. */}
                {contaCnpjPendente && (
                  <div className="rounded-lg bg-amber-50 border border-amber-300 p-3 space-y-2">
                    <p className="text-xs text-amber-800">
                      <AlertTriangle size={14} className="inline mr-1 -mt-0.5 text-amber-600" />
                      Esta conta foi criada pela importação e está <strong>sem CNPJ</strong>. Informe o CNPJ da empresa para criar a oportunidade.
                    </p>
                    <div className="max-w-xs">
                      <label htmlFor={fid.cnpjConta} className="block text-xs font-medium text-gray-600 mb-1">
                        CNPJ {cnpjLoading && <span className="text-[#00DBFF]">buscando…</span>} *
                      </label>
                      <input id={fid.cnpjConta} type="text" value={form.cnpj || ''}
                        onChange={e => handleChange('cnpj', e.target.value)}
                        onBlur={e => fetchCnpj(e.target.value)}
                        placeholder="00.000.000/0001-00" className={inputClass('cnpj')} />
                      {errors.cnpj && <p className="text-xs text-red-500 mt-1">{errors.cnpj}</p>}
                      <p className="text-xs text-gray-500 mt-1">
                        O CNPJ fica na oportunidade. Para corrigir o cadastro da conta, peça a atualização em Contas (PJ).
                      </p>
                    </div>
                  </div>
                )}

                {contaAlertMatriz && (
                  <div className="rounded-lg bg-amber-50 border border-amber-300 p-3 text-amber-800 text-xs flex items-center gap-2">
                    <AlertTriangle size={15} className="text-amber-600 shrink-0" />
                    <p className="font-semibold">⚠️ Esta empresa é filial de <strong>{contaAlertMatriz.nome}</strong>. O decisor pode estar lá.</p>
                  </div>
                )}
                {contaAlertFiliais > 0 && (
                  <div className="rounded-lg bg-sky-50 border border-sky-200 p-3 text-sky-800 text-xs flex items-center gap-2">
                    <Building2 size={15} className="text-sky-500 shrink-0" />
                    <p className="font-semibold">🏢 Esta empresa possui <strong>{contaAlertFiliais}</strong> filiais cadastradas no CRM.</p>
                  </div>
                )}
              </div>
            )}

            {/* Insights CNPJ */}
            {(() => {
              const showInsights = !!form.cnpj && (!!form.cnae_principal_codigo || !!form.capital_social || (form.qsa && form.qsa.length > 0));
              if (!showInsights) return null;
              const cnae = String(form.cnae_principal_codigo || '').replace(/\D/g, '');
              const isFit = ['13', '14', '15', '4641', '4642', '4781', '4782'].some(pfx => cnae.startsWith(pfx));
              const cap = parseFloat(form.capital_social) || 0;
              let porteLabel = 'PME', porteColorClass = 'bg-[#00DBFF]/10 text-[#00DBFF] border-[#00DBFF]/30 shadow-[0_0_5px_rgba(0,219,255,0.2)]';
              if (cap >= 100000 && cap <= 1000000) { porteLabel = 'Middle Market'; porteColorClass = 'bg-amber-500/10 text-amber-500 border-amber-500/30'; }
              else if (cap > 1000000) { porteLabel = 'Enterprise'; porteColorClass = 'bg-purple-500/10 text-purple-500 border-purple-500/30 shadow-[0_0_5px_rgba(168,85,247,0.2)]'; }
              return (
                <div className="border border-gray-200 rounded-lg overflow-hidden bg-gray-50/50">
                  <button type="button" onClick={() => setInsightsOpen(!insightsOpen)}
                    className="w-full flex items-center justify-between px-4 py-3 bg-gray-100 hover:bg-gray-200/60 transition-colors text-left">
                    <span className="text-xs font-bold text-[#535c69] flex items-center gap-2 uppercase tracking-wider">
                      <Sparkles size={14} className="text-[#00DBFF]" />
                      Insights de Inteligência Comercial (CNPJ)
                    </span>
                    {insightsOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                  </button>
                  {insightsOpen && (
                    <div className="p-4 space-y-4 border-t border-gray-200">
                      <div className="flex flex-wrap gap-2">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${isFit ? 'bg-[#00FFD4]/10 text-[#00FFD4] border-[#00FFD4]/30' : 'bg-gray-200 text-gray-700 border-gray-300'}`}>
                          {isFit ? 'FIT ELEVADO (Moda/Têxtil)' : 'Fit Tradicional'}
                        </span>
                        {cap > 0 && <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${porteColorClass}`}>Porte: {porteLabel} ({formatBRL(cap)})</span>}
                      </div>
                      {form.cnae_principal_codigo && <div className="text-xs text-gray-600"><span className="font-semibold text-gray-700">CNAE Principal:</span> {form.cnae_principal_codigo} - {form.cnae_principal_descricao}</div>}
                      {form.qsa && form.qsa.length > 0 && (
                        <div className="space-y-2">
                          <p className="text-xs font-semibold text-gray-700">Quadro de Sócios e Administradores (QSA):</p>
                          <div className="max-h-28 overflow-y-auto border border-gray-200 rounded-lg p-2 bg-white space-y-1 divide-y divide-gray-100">
                            {form.qsa.map((socio, idx) => (
                              <div key={idx} className="text-xs py-1 first:pt-0 last:pb-0 flex justify-between gap-3">
                                <span className="font-medium text-gray-800">{socio.nome}</span>
                                <span className="text-gray-500 italic shrink-0">{socio.qualificacao}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })()}

            <fieldset>
              <legend className="text-sm font-semibold text-[#535c69] mb-2">ESN Responsável</legend>
              <CreatableSelect options={esnOptions} value={form.esn_id} onChange={val => handleChange('esn_id', val)} onCreate={onCreateEsn} placeholder="Selecione o ESN..." />
            </fieldset>

            <fieldset className="space-y-3">
              <legend className="text-sm font-semibold text-[#535c69] mb-2">Contato</legend>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label htmlFor={fid.contatoNome} className="block text-xs font-medium text-gray-600 mb-1">Nome do Contato</label>
                  <input id={fid.contatoNome} type="text" value={form.contato_nome || ''} onChange={e => handleChange('contato_nome', e.target.value)} className={inputClass('contato_nome')} />
                </div>
                <div>
                  <label htmlFor={fid.contatoFuncao} className="block text-xs font-medium text-gray-600 mb-1">Função</label>
                  <input id={fid.contatoFuncao} type="text" value={form.contato_funcao || ''} onChange={e => handleChange('contato_funcao', e.target.value)} className={inputClass('contato_funcao')} />
                </div>
                <div>
                  <label htmlFor={fid.contatoTelefone} className="block text-xs font-medium text-gray-600 mb-1">Telefone</label>
                  <div className="flex gap-2">
                    <input id={fid.contatoTelefone} type="text" value={form.contato_telefone || ''} onChange={e => handleChange('contato_telefone', e.target.value)} placeholder="(00) 00000-0000" className={`${inputClass('contato_telefone')} flex-1`} />
                    {form.contato_telefone && (
                      <button type="button" onClick={() => setShowWA(true)}
                        className="flex items-center gap-1 px-2.5 py-1.5 bg-[#25D366] text-white rounded-lg text-xs font-medium hover:bg-[#1ebe5d] transition-colors" title="Enviar WhatsApp">
                        <MessageCircle size={13} />
                      </button>
                    )}
                  </div>
                </div>
                <div>
                  <label htmlFor={fid.contatoEmail} className="block text-xs font-medium text-gray-600 mb-1">E-mail</label>
                  <input id={fid.contatoEmail} type="text" value={form.contato_email || ''}
                    onChange={e => handleChange('contato_email', e.target.value)}
                    onBlur={e => {
                      if (e.target.value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e.target.value)) {
                        setErrors(err => ({ ...err, contato_email: 'E-mail inválido' }));
                      } else {
                        setErrors(err => ({ ...err, contato_email: null }));
                      }
                    }}
                    placeholder="email@dominio.com" className={inputClass('contato_email')} />
                  {errors.contato_email && <p className="text-xs text-red-500 mt-1">{errors.contato_email}</p>}
                </div>
              </div>
            </fieldset>

            <fieldset>
              <legend className="text-sm font-semibold text-[#535c69] mb-2">Produto</legend>
              <CreatableSelect options={products} value={form.produto_id} onChange={val => handleChange('produto_id', val)} onCreate={onCreateProduct} placeholder="Selecione o produto..." />
            </fieldset>

            <fieldset className="space-y-3">
              <legend className="text-sm font-semibold text-[#535c69] mb-2">
                Valores
                <span className="ml-2 text-xs font-normal text-gray-400">Total: {formatBRL((Number(form.valor_saas)||0)+(Number(form.valor_adesao)||0)+(Number(form.valor_servico)||0))}</span>
              </legend>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label htmlFor={fid.valorSaas} className="block text-xs font-medium text-gray-600 mb-1">Valor SaaS</label>
                  <CurrencyInput id={fid.valorSaas} value={form.valor_saas} onChange={v => handleChange('valor_saas', v)} className={inputClass('valor_saas')} />
                </div>
                <div>
                  <label htmlFor={fid.valorAdesao} className="block text-xs font-medium text-gray-600 mb-1">Valor Adesão</label>
                  <CurrencyInput id={fid.valorAdesao} value={form.valor_adesao} onChange={v => handleChange('valor_adesao', v)} className={inputClass('valor_adesao')} />
                </div>
                <div>
                  <label htmlFor={fid.valorServico} className="block text-xs font-medium text-gray-600 mb-1">Valor Serviço</label>
                  <CurrencyInput id={fid.valorServico} value={form.valor_servico} onChange={v => handleChange('valor_servico', v)} className={inputClass('valor_servico')} />
                </div>
              </div>
            </fieldset>

            {((Number(form.valor_adesao)||0) > 0 || (Number(form.valor_servico)||0) > 0) && (
              <fieldset className="space-y-4">
                <legend className="text-sm font-semibold text-[#535c69] mb-2">Parcelamento</legend>
                {(Number(form.valor_adesao)||0) > 0 && (
                  <ParcelamentoBlock label="Adesão" valor={Number(form.valor_adesao)||0}
                    modo={form.adesao_modo_parcela || 'igual'} entradaTipo={form.adesao_entrada_tipo || 'pct'}
                    entradaValor={form.adesao_entrada_valor} saldoParcelas={form.adesao_saldo_parcelas || 1}
                    numParcelas={form.num_parcelas_implantacao || 1}
                    onModo={v => handleChange('adesao_modo_parcela', v)} onEntradaTipo={v => handleChange('adesao_entrada_tipo', v)}
                    onEntradaValor={v => handleChange('adesao_entrada_valor', v)} onSaldoParcelas={v => handleChange('adesao_saldo_parcelas', v)}
                    onNumParcelas={v => handleChange('num_parcelas_implantacao', v)} inputClass={inputClass} />
                )}
                {(Number(form.valor_servico)||0) > 0 && (
                  <ParcelamentoBlock label="Serviço" valor={Number(form.valor_servico)||0}
                    modo={form.servico_modo_parcela || 'igual'} entradaTipo={form.servico_entrada_tipo || 'pct'}
                    entradaValor={form.servico_entrada_valor} saldoParcelas={form.servico_saldo_parcelas || 1}
                    numParcelas={form.num_parcelas_licenca || 1}
                    onModo={v => handleChange('servico_modo_parcela', v)} onEntradaTipo={v => handleChange('servico_entrada_tipo', v)}
                    onEntradaValor={v => handleChange('servico_entrada_valor', v)} onSaldoParcelas={v => handleChange('servico_saldo_parcelas', v)}
                    onNumParcelas={v => handleChange('num_parcelas_licenca', v)} inputClass={inputClass} />
                )}
              </fieldset>
            )}

            <fieldset>
              <legend className="text-sm font-semibold text-[#535c69] mb-2">
                Origem do Lead{(funnelType || form.funnel_type) === 'new_names' ? ' *' : ''}
              </legend>
              <select value={form.origem_lead || ''} onChange={e => handleChange('origem_lead', e.target.value)} className={inputClass('origem_lead')}>
                <option value="">Selecione a origem...</option>
                {origins.map(o => <option key={o.id} value={o.nome}>{o.nome}</option>)}
              </select>
              {errors.origem_lead && <p className="text-xs text-red-500 mt-1">{errors.origem_lead}</p>}
            </fieldset>

            <fieldset>
              <legend className="text-sm font-semibold text-[#535c69] mb-2">Previsão de Fechamento *</legend>
              <input id={fid.previsao} type="month" value={form.previsao_fechamento || ''} onChange={e => handleChange('previsao_fechamento', e.target.value)}
                min={`${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`}
                className={inputClass('previsao_fechamento')} aria-label="Previsão de fechamento" />
              {errors.previsao_fechamento && <p className="text-xs text-red-500 mt-1">{errors.previsao_fechamento}</p>}
            </fieldset>

            <CustomFieldsSection entityType="lead" entityId={lead?.id || null} />

            {lead?.apn_dor && (
              <div className="rounded-lg bg-purple-50 border border-purple-200 p-3">
                <div className="text-[10px] font-semibold text-purple-700 uppercase tracking-wide mb-1">Dor levantada pelo Agente de Prospecção</div>
                <p className="text-sm text-gray-700">{lead.apn_dor}</p>
                {lead.apn_agenda_at && (
                  <div className="text-xs text-purple-600 mt-1">
                    Agenda: {new Date(lead.apn_agenda_at).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}
                  </div>
                )}
              </div>
            )}

          </>)}

          {/* Actions */}
          {errors._funnel && <p className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{errors._funnel}</p>}
          {errors._submit && <p className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{errors._submit}</p>}
          {activeTab !== 'interacoes' && activeTab !== 'contatos' && (
            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
              <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">Cancelar</button>
              <button type="submit"
                disabled={form.situacao_cadastral && form.situacao_cadastral.toUpperCase() !== 'ATIVA'}
                className={`px-4 py-2 text-sm text-white rounded-lg transition-all duration-300 ${
                  form.situacao_cadastral && form.situacao_cadastral.toUpperCase() !== 'ATIVA'
                    ? 'bg-gray-400 cursor-not-allowed opacity-50'
                    : 'bg-[#00DBFF] hover:bg-[#00DBFF]/80'
                }`}>
                {lead ? 'Salvar' : 'Criar Lead'}
              </button>
            </div>
          )}
        </form>
      </div>

      {selectedContaForModal && (
        <ContaFormModal conta={selectedContaForModal} initialTab={activeContaTab}
          onClose={() => setSelectedContaForModal(null)} onSaved={() => setSelectedContaForModal(null)} />
      )}

      {showWA && (
        <WhatsAppModal lead={{ ...form, produto_nome: products?.find(p => p.id === form.produto_id)?.nome }}
          userName={user?.username} onClose={() => setShowWA(false)} />
      )}
    </div>
  );
}
